import { useEffect, useState, type JSX } from "react"
import alert from "./Alert.module.css"
import type { AlertEntity } from "./AlertEntity"

import { IoMdCloudDone, IoMdWarning } from "react-icons/io"
import { MdDangerous } from "react-icons/md"

interface AlertProps {
    showAlert: boolean
    setShowAlert: (value: boolean) => void
    alertEntity: AlertEntity | null
}

function Alert(props: AlertProps): JSX.Element {
    const { showAlert, setShowAlert, alertEntity } = props

    const message = alertEntity?.message ?? ''
    const delay = alertEntity?.delay ?? 10
    const onClose = alertEntity?.onClose ?? (() => { })
    const onOk = alertEntity?.onOk ?? (() => { })
    const withOK = alertEntity?.withOk ?? false
    const withCancel = alertEntity?.withCancel ?? false
    const withInput = alertEntity?.withInput ?? false
    const level = alertEntity?.level ?? 1

    const [inputValue, setInputValue] = useState("")
    const [alertLevel, setAlertLevel] = useState(2)
    const [countingDown, setCountingDown] = useState(delay ?? 10)

    useEffect(() => {
        setAlertLevel(level)
    }, [level])

    useEffect(() => {
        if (showAlert) setCountingDown(delay)
    }, [delay, showAlert])

    useEffect(() => {
        if (!showAlert) return

        const interval = setInterval(() => {
            setCountingDown((prev) => {
                if (prev <= 1) {
                    if (withOK !== true) {
                        onClose?.()
                        setShowAlert(false)
                    }
                    return 0
                }
                return prev - 1
            })
        }, 1000)

        return () => clearInterval(interval)
    }, [showAlert, withOK, onClose, setShowAlert])

    useEffect(() => {
        if (showAlert && withOK !== true) {
            const timeoutId = setTimeout(() => {
                if (!withCancel) {
                    onClose?.()
                    setShowAlert(false)
                }
            }, delay * 1000)
            return () => clearTimeout(timeoutId)
        }
    }, [showAlert, delay, withCancel, withOK, onClose, setShowAlert])

    function clickOK() {
        if (typeof onOk === "function") {
            onOk()
            return setShowAlert(false)
        }
        setCountingDown(0)
        onClose?.()
        return setShowAlert(false)
    }

    function clickCancel() {
        setInputValue("")
        setCountingDown(0)
        onClose?.()
        return setShowAlert(false)
    }

    return <>
        {showAlert && <div className="backdrop-alert" onClick={clickCancel}>

            <div className={`${alert.container} ${withInput ? alert.smallWidth : alert.defaultWidth}`} onClick={(e) => e.stopPropagation()}>
                <div
                    className={
                        alertLevel === 1
                            ? alert.symbolDone
                            : alertLevel === 2
                                ? alert.symbolWarn
                                : alert.symbolCritical
                    }
                >
                    {alertLevel === 1 ? (
                        <IoMdCloudDone className={alert.iconAlert} />
                    ) : alertLevel === 2 ? (
                        <IoMdWarning className={alert.iconAlert} />
                    ) : (
                        <MdDangerous className={alert.iconAlert} />
                    )}
                </div>

                <div className={alert.content}>
                    <div className={alert.info}>{message}</div>
                    {withInput && (
                        <div className={alert.input}>
                            <input
                                className="input-form"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                required={true}
                                autoComplete="off"
                            />
                        </div>
                    )}
                    <div className={alert.buttons}>
                        {(withCancel || !withOK) && (
                            <button className="btn-danger" onClick={clickCancel}>
                                Close{!withOK ? `(${countingDown})` : null}
                            </button>
                        )}

                        {((withOK === true) &&
                            <button className="btn-primary" onClick={clickOK}>
                                OK
                            </button>
                        )}
                    </div>
                </div>
            </div>

        </div>}
    </>
}

export { Alert }
